import { useCallback, useMemo, useState } from "react";
import {
  ObjectsTableColumn,
  ObjectsTableColumnId,
  OBJECTS_PROTECTED_COLUMN_IDS
} from "app/data/constants/objectsConstants";
import {
  getVisibleObjectColumns,
  mergeHiddenColumnIds,
  readHiddenColumnIdsFromSession,
  writeHiddenColumnIdsToSession
} from "app/data/helpers/objectsColumnVisibilityHelpers";

interface UseColumnVisibilityProps {
  storageKey: string;
  columns: ObjectsTableColumn[];
  protectedIds?: ObjectsTableColumnId[];
}

export const useColumnVisibility = ({
  storageKey,
  columns,
  protectedIds = OBJECTS_PROTECTED_COLUMN_IDS
}: UseColumnVisibilityProps) => {
  const columnIds = useMemo(
    () => columns.map((column) => column.id),
    [columns]
  );

  const [hiddenIds, setHiddenIds] = useState<ObjectsTableColumnId[]>(() =>
    mergeHiddenColumnIds(
      readHiddenColumnIdsFromSession(storageKey),
      columnIds,
      protectedIds
    )
  );

  const visibleColumns = useMemo(
    () => getVisibleObjectColumns(columns, hiddenIds),
    [columns, hiddenIds]
  );

  const applyHiddenIds = useCallback(
    (nextHidden: ObjectsTableColumnId[]) => {
      const merged = mergeHiddenColumnIds(nextHidden, columnIds, protectedIds);
      setHiddenIds(merged);
      writeHiddenColumnIdsToSession(storageKey, merged);
    },
    [columnIds, protectedIds, storageKey]
  );

  const resetToDefault = useCallback(() => {
    applyHiddenIds([]);
  }, [applyHiddenIds]);

  return {
    hiddenIds,
    visibleColumns,
    protectedIds,
    applyHiddenIds,
    resetToDefault
  };
};
